/**
 * RFC-004：跨组织项目协作（PCO）相关 React Query Hooks
 *
 * 约定：
 *   - 所有 PCO 列表 / Pending / 详情查询走 projectApi.listCollaboratorOrgs / listPendingCollaboratorOrgs
 *   - 写操作通过 mutation 成功后 invalidate 相关 query key，触发列表刷新
 *   - Pending 列表绑定到 activeOrgId，因为是 "当前组织待审批的邀请"
 */
import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { folderKeys } from '@/hooks/api/useFolders';
import { projectKeys } from '@/hooks/api/useProjects';
import { orgProjectBudgetTreeKeys } from '@/hooks/api/useOrgProjectBudgetTree';
import { projectApi } from '@/services/api';
import { useOrgContextStore } from '@/stores/orgContextStore';
import type {
  AddCollaboratorOrgMemberDto,
  CollaboratorOrgListResponse,
  ContractorSettlementReportTerminateDto,
  BatchInviteCollaboratorOrgDto,
  InviteCollaboratorOrgDto,
  PendingCollaborationListResponse,
  UpdateCollaboratorOrgAllowedFunctionalRolesDto,
  UpdateCollaboratorOrgMemberRolesDto,
} from '@/types';

export const collaboratorOrgKeys = {
  all: ['collaborator-orgs'] as const,
  byProjectRoot: (projectId: string | null) =>
    [...collaboratorOrgKeys.all, 'project', projectId] as const,
  byProject: (activeOrgId: string | null, projectId: string | null) =>
    [...collaboratorOrgKeys.byProjectRoot(projectId), activeOrgId] as const,
  memberCandidatesRoot: (projectId: string | null, pcoId: string | null) =>
    [...collaboratorOrgKeys.all, 'project', projectId, 'pco', pcoId, 'member-candidates'] as const,
  memberCandidates: (activeOrgId: string | null, projectId: string | null, pcoId: string | null) =>
    [...collaboratorOrgKeys.memberCandidatesRoot(projectId, pcoId), activeOrgId] as const,
  pending: (activeOrgId: string | null) =>
    [...collaboratorOrgKeys.all, 'pending', activeOrgId] as const,
};

function invalidateOrgBudgetTrees(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: orgProjectBudgetTreeKeys.all, exact: false });
}

/**
 * 查询某项目的协作组织列表
 */
export function useProjectCollaboratorOrgs(projectId: string | null, enabled = true) {
  const activeOrgId = useOrgContextStore((s) => s.activeOrgId);
  return useQuery<CollaboratorOrgListResponse>({
    queryKey: collaboratorOrgKeys.byProject(activeOrgId, projectId),
    queryFn: () => projectApi.listCollaboratorOrgs(projectId!),
    enabled: !!projectId && enabled,
    staleTime: 1000 * 30,
  });
}

const CLOSED_COLLABORATOR_STATUSES = new Set(['revoked', 'rejected', 'expired']);

export function resolveContractorParentPermissionDefaults({
  orgId,
  channelOrgs,
  collaborators,
}: {
  orgId: string;
  channelOrgs: Array<{ orgId: string; canCreateScreenplays?: boolean }>;
  collaborators: Array<{
    collabOrgId: string;
    status?: string;
    canManageAllAssets?: boolean;
  }>;
}): { canCreateScreenplays: boolean; canManageAllAssets: boolean } {
  const canCreateScreenplays = channelOrgs.some(
    (org) => org.orgId === orgId && org.canCreateScreenplays !== false
  );
  const openRows = collaborators.filter(
    (item) => item.collabOrgId === orgId && !CLOSED_COLLABORATOR_STATUSES.has(item.status ?? '')
  );
  return {
    canCreateScreenplays,
    canManageAllAssets: openRows.some((item) => Boolean(item.canManageAllAssets)),
  };
}

export function useLoadContractorInviteParentPermissions() {
  const queryClient = useQueryClient();
  const activeOrgId = useOrgContextStore((state) => state.activeOrgId);
  return useCallback(
    async ({
      orgId,
      projectId,
      grantParentProjectId,
      loadedProject,
    }: {
      orgId: string;
      projectId: string;
      grantParentProjectId?: string | null;
      loadedProject?: { parentProjectId?: string | null } | null;
    }) => {
      let parentId = grantParentProjectId || null;
      if (!parentId) {
        const parentFieldReady = loadedProject != null && 'parentProjectId' in loadedProject;
        const project =
          parentFieldReady && loadedProject
            ? loadedProject
            : await queryClient.fetchQuery({
                queryKey: projectKeys.detail(activeOrgId, projectId),
                queryFn: () => projectApi.getById(projectId),
                staleTime: 0,
              });
        parentId = project?.parentProjectId || projectId;
      }
      if (!parentId) {
        throw new Error('缺少父项目，无法读取承制权限');
      }
      const [grants, parentCollaborators] = await Promise.all([
        queryClient.fetchQuery({
          queryKey: projectKeys.screenplayCreateGrants(parentId),
          queryFn: () => projectApi.listScreenplayCreateGrants(parentId),
          staleTime: 0,
        }),
        queryClient.fetchQuery({
          queryKey: collaboratorOrgKeys.byProject(activeOrgId, parentId),
          queryFn: () => projectApi.listCollaboratorOrgs(parentId),
          staleTime: 0,
        }),
      ]);
      return resolveContractorParentPermissionDefaults({
        orgId,
        channelOrgs: grants.channelOrgs ?? [],
        collaborators: parentCollaborators.items ?? [],
      });
    },
    [activeOrgId, queryClient]
  );
}

/**
 * 查询当前活动组织的待审批跨组织邀请
 */
export function usePendingCollaboratorOrgs(enabled = true) {
  const activeOrgId = useOrgContextStore((s) => s.activeOrgId);
  return useQuery<PendingCollaborationListResponse>({
    queryKey: collaboratorOrgKeys.pending(activeOrgId),
    queryFn: () => projectApi.listPendingCollaboratorOrgs(),
    enabled: !!activeOrgId && enabled,
    staleTime: 1000 * 30,
  });
}

export function useCollaboratorOrgMemberCandidates(
  projectId: string | null,
  pcoId: string | null,
  enabled = true
) {
  const activeOrgId = useOrgContextStore((s) => s.activeOrgId);
  return useQuery({
    queryKey: collaboratorOrgKeys.memberCandidates(activeOrgId, projectId, pcoId),
    queryFn: () => projectApi.listCollaboratorOrgMemberCandidates(projectId!, pcoId!),
    enabled: !!projectId && !!pcoId && enabled,
    staleTime: 1000 * 30,
  });
}

interface InviteArgs {
  projectId: string;
  data: InviteCollaboratorOrgDto;
}

export function useBatchInviteCollaboratorOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      parentProjectId,
      data,
    }: {
      parentProjectId: string;
      data: BatchInviteCollaboratorOrgDto;
    }) => projectApi.batchInviteCollaboratorOrg(parentProjectId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: collaboratorOrgKeys.all });
      qc.invalidateQueries({ queryKey: projectKeys.all });
    },
  });
}

export function useInviteCollaboratorOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, data }: InviteArgs) =>
      projectApi.inviteCollaboratorOrg(projectId, data),
    onSuccess: (_resp, vars) => {
      qc.invalidateQueries({ queryKey: collaboratorOrgKeys.byProjectRoot(vars.projectId) });
    },
  });
}

export function useAcceptCollaboratorOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (pcoId: string) => projectApi.acceptCollaboratorOrg(pcoId),
    onSuccess: (resp) => {
      qc.invalidateQueries({ queryKey: collaboratorOrgKeys.all });
      qc.invalidateQueries({ queryKey: ['projects'] });
      qc.invalidateQueries({ queryKey: folderKeys.trees() });
      // 成功后同时刷新 project 详情（若已缓存）
      if (resp?.projectId) {
        qc.invalidateQueries({ queryKey: collaboratorOrgKeys.byProjectRoot(resp.projectId) });
      }
      invalidateOrgBudgetTrees(qc);
    },
    onError: () => {
      qc.invalidateQueries({ queryKey: collaboratorOrgKeys.all });
    },
  });
}

export function useWithdrawCollaboratorOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, pcoId }: { projectId: string; pcoId: string }) =>
      projectApi.withdrawCollaboratorOrg(projectId, pcoId),
    onSuccess: (_resp, vars) => {
      qc.invalidateQueries({ queryKey: collaboratorOrgKeys.byProjectRoot(vars.projectId) });
      qc.invalidateQueries({ queryKey: ['projects', 'subproject-member-management'] });
    },
  });
}

export function useRejectCollaboratorOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (pcoId: string) => projectApi.rejectCollaboratorOrg(pcoId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: collaboratorOrgKeys.all });
      qc.invalidateQueries({ queryKey: folderKeys.trees() });
      invalidateOrgBudgetTrees(qc);
    },
    onError: () => {
      qc.invalidateQueries({ queryKey: collaboratorOrgKeys.all });
    },
  });
}

interface RevokeArgs {
  pcoId: string;
  projectId?: string | null;
  data: ContractorSettlementReportTerminateDto;
}

export function useRevokeCollaboratorOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ pcoId, data }: RevokeArgs) => projectApi.revokeCollaboratorOrg(pcoId, data),
    onSuccess: (_resp, vars) => {
      qc.invalidateQueries({ queryKey: collaboratorOrgKeys.all });
      qc.invalidateQueries({ queryKey: ['projects'] });
      qc.invalidateQueries({ queryKey: folderKeys.trees() });
      if (vars.projectId) {
        qc.invalidateQueries({ queryKey: collaboratorOrgKeys.byProjectRoot(vars.projectId) });
      }
      invalidateOrgBudgetTrees(qc);
    },
  });
}

interface UpdateAllowedFunctionalRolesArgs {
  projectId: string;
  pcoId: string;
  data: UpdateCollaboratorOrgAllowedFunctionalRolesDto;
}

interface UpdateCollaboratorOrgDownloadArgs {
  projectId: string;
  pcoId: string;
  canDownload: boolean;
}

export function useUpdateCollaboratorOrgManageAllAssets() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      projectId,
      pcoId,
      canManageAllAssets,
    }: {
      projectId: string;
      pcoId: string;
      canManageAllAssets: boolean;
    }) => projectApi.updateCollaboratorOrgManageAllAssets(projectId, pcoId, { canManageAllAssets }),
    onSuccess: (_resp, vars) => {
      qc.invalidateQueries({ queryKey: collaboratorOrgKeys.all });
      qc.invalidateQueries({ queryKey: collaboratorOrgKeys.byProjectRoot(vars.projectId) });
    },
  });
}

export function useUpdateCollaboratorOrgDownload() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, pcoId, canDownload }: UpdateCollaboratorOrgDownloadArgs) =>
      projectApi.updateCollaboratorOrgDownload(projectId, pcoId, { canDownload }),
    onSuccess: (_resp, vars) => {
      qc.invalidateQueries({ queryKey: collaboratorOrgKeys.byProjectRoot(vars.projectId) });
      qc.invalidateQueries({
        queryKey: collaboratorOrgKeys.memberCandidatesRoot(vars.projectId, vars.pcoId),
      });
      qc.invalidateQueries({ queryKey: ['project-shares', 'members', vars.projectId] });
    },
  });
}

export function useUpdateCollaboratorOrgAllowedFunctionalRoles() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, pcoId, data }: UpdateAllowedFunctionalRolesArgs) =>
      projectApi.updateCollaboratorOrgAllowedFunctionalRoles(projectId, pcoId, data),
    onSuccess: (_resp, vars) => {
      qc.invalidateQueries({ queryKey: collaboratorOrgKeys.byProjectRoot(vars.projectId) });
      qc.invalidateQueries({
        queryKey: collaboratorOrgKeys.memberCandidatesRoot(vars.projectId, vars.pcoId),
      });
      qc.invalidateQueries({ queryKey: ['project-shares', 'members', vars.projectId] });
    },
  });
}

interface AddPcoMemberArgs {
  projectId: string;
  pcoId: string;
  data: AddCollaboratorOrgMemberDto;
}

export function useAddCollaboratorOrgMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, pcoId, data }: AddPcoMemberArgs) =>
      projectApi.addCollaboratorOrgMember(projectId, pcoId, data),
    onSuccess: (_resp, vars) => {
      qc.invalidateQueries({ queryKey: collaboratorOrgKeys.byProjectRoot(vars.projectId) });
      qc.invalidateQueries({
        queryKey: collaboratorOrgKeys.memberCandidatesRoot(vars.projectId, vars.pcoId),
      });
      qc.invalidateQueries({ queryKey: ['project-shares', 'members', vars.projectId] });
      qc.invalidateQueries({ queryKey: ['temp-members', 'project', vars.projectId] });
    },
  });
}

interface UpdatePcoMemberRolesArgs {
  projectId: string;
  pcoId: string;
  userId: string;
  data: UpdateCollaboratorOrgMemberRolesDto;
}

export function useUpdateCollaboratorOrgMemberRoles() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, pcoId, userId, data }: UpdatePcoMemberRolesArgs) =>
      projectApi.updateCollaboratorOrgMemberRoles(projectId, pcoId, userId, data),
    onSuccess: (_resp, vars) => {
      qc.invalidateQueries({ queryKey: collaboratorOrgKeys.byProjectRoot(vars.projectId) });
      qc.invalidateQueries({
        queryKey: collaboratorOrgKeys.memberCandidatesRoot(vars.projectId, vars.pcoId),
      });
      qc.invalidateQueries({ queryKey: ['project-shares', 'members', vars.projectId] });
    },
  });
}
